import React, { useState, useEffect, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Pagination,
    Box,
    Button,
    TextField
} from '@mui/material';
import colors from '../theme/Colors';
import CustomSelect from './CustomSelect';
import { api } from '../helpers/apiRequest';
const TABLE_HEADERS = [
    { id: 'details', label: 'DETAILS', minWidth: 150 },
    { id: 'date', label: 'DATE', minWidth: 120, type: 'date' },
    { id: 'amount', label: 'AMOUNT', minWidth: 120 },
    { id: 'duty', label: 'DUTY', minWidth: 120 },
    { id: 'gst', label: 'GST', minWidth: 120, type: 'number' },
    { id: 'qst', label: 'QST', minWidth: 120, type: 'number' },
    { id: 'actions', label: 'Actions', minWidth: 150 }
];

const PurchaseTable = (accessControl) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalPages, setTotalPages] = useState(1);
    const [purchaseData, setPurchaseData] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [originalData, setOriginalData] = useState([]);
    const [isAddingNewRow, setIsAddingNewRow] = useState(false);
    const [newRowData, setNewRowData] = useState({
        details: '',
        date: '',
        amount: '',
        duty: '',
        gst_paid: '',
        qst_paid: ''
    });
    const [autoCalculate, setAutoCalculate] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [userInfo] = useState(JSON.parse(localStorage.getItem('user')));
    const fetchPurchases = async (rpp) => {
        try {
            const response = await api.get(
                `/purchases/all-purchases?page=${page + 1}&limit=${rpp}`
            );
            setPurchaseData(response.data.data);
            setOriginalData(response.data.data);
            setTotalPages(Math.ceil(response.data.total_count / rowsPerPage));
        } catch (error) {
            console.error('Error fetching purchases:', error);
        }
    };

    useEffect(() => {
        fetchPurchases();
    }, [page, rowsPerPage]);
    const rppRef=useRef(rowsPerPage);
    useEffect(()=>{
        rppRef.current=rowsPerPage;
    },[rowsPerPage]);
    useEffect(() => {
        if (!isAddingNewRow && !editingId) {
            const interval = setInterval(() => {
                fetchPurchases(rppRef.current);
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [isAddingNewRow, editingId]);
   
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleNewRowChange = (field, value) => {
        setNewRowData(prev => {
            const updatedData = {
                ...prev,
                [field]: value
            };

            // Only auto-calculate if autoCalculate is true and the field is amount or duty
            if (autoCalculate && (field === 'amount' || field === 'duty')) {
                const amount = parseFloat(updatedData.amount) || 0;
                const duty = parseFloat(updatedData.duty) || 0;
                const total = amount + duty;

                updatedData.gst = ((5 / 100) * total).toFixed(2);
                updatedData.qst = ((9.975 / 100) * total).toFixed(2);
            }

            return updatedData; 
        });
    };

    const addPurchase = () => {
        setIsAddingNewRow(true);
    };

    const formatDate = (date) => {
        if (!date) return '';
        const dateObj = new Date(date);
        return isNaN(dateObj.getTime()) ? '' : dateObj.toISOString().split('T')[0];
    };

    const handleInputChange = (index, field, value) => {
        const updatedData = [...purchaseData];
        
        // Handle date formatting for display
        if (field === 'date') {
            updatedData[index][field] = value;
        } else {
            updatedData[index][field] = value;
        }
        
        if (autoCalculate && (field === 'amount' || field === 'duty')) {
            const row = updatedData[index];
            const amount = parseFloat(row.amount) || 0;
            const duty = parseFloat(row.duty) || 0;
            const total = amount + duty;

            updatedData[index].gst = ((5 / 100) * total).toFixed(2);
            updatedData[index].qst = ((9.975 / 100) * total).toFixed(2);
        }

        setPurchaseData(updatedData);
    };

    const handleSave = async () => {
        try {
            const payload = {
                details: newRowData.details,
                date: newRowData.date,
                amount: parseFloat(newRowData.amount) || 0,
                duty: parseFloat(newRowData.duty) || 0,
                gst: parseFloat(newRowData.gst) || 0,
                qst: parseFloat(newRowData.qst) || 0
            };

            await api.post('/purchases/create-purchase', payload);
            setIsAddingNewRow(false);
            setNewRowData({
                details: '',
                date: '',
                amount: '',
                duty: '',
                gst: '',
                qst: ''
            });
            fetchPurchases();
        } catch (error) {
            console.error('Error saving purchase:', error);
        }
    };

    const handleCancel = () => {
        setIsAddingNewRow(false);
        setNewRowData({
            details: '',
            date: '',
            amount: '',
            duty: '',
            gst_paid: '',
            qst_paid: ''
        });
    };

    const handleEdit = (row) => {
        setEditingId(row._id);
    };

    const handleUpdate = async (row) => {
        try {
            const payload = {
                details: row.details,
                date: row.date,
                amount: parseFloat(row.amount) || 0,
                duty: parseFloat(row.duty) || 0,
                gst: parseFloat(row.gst) || 0,
                qst: parseFloat(row.qst) || 0
            };

            await api.put(`/purchases/purchase/${row._id}`, payload);
            setEditingId(null);
            fetchPurchases();
        } catch (error) {
            console.error('Error updating purchase:', error);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setPurchaseData(originalData);
    };

    return (
        <TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={() => setAutoCalculate(!autoCalculate)}
                    sx={{
                        backgroundColor: colors.primary,
                    }}
                >
                    {autoCalculate ? 'Switch to Manual' : 'Switch to Auto'}
                </Button>

                {(userInfo?.role === 'admin' || accessControl["manage-purchase-expenses"]) && <Button
                    variant="contained"
                    sx={{
                        backgroundColor: colors.primary,
        
                    }}
                    onClick={addPurchase}
                    disabled={isAddingNewRow}
                >
                    Add Purchase
                </Button>}
            </Box>
            <Table>
                <TableHead>
                    <TableRow>
                        {TABLE_HEADERS.map((header) => (
                            <TableCell
                                key={header.id}
                                sx={{
                                    border: '1px solid black',
                                    padding: '8px',
                                    backgroundColor: colors.primary,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    minWidth: header.minWidth
                                }}
                            >
                                {header.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isAddingNewRow && (
                        <TableRow>
                            {TABLE_HEADERS.map((header) => (
                                <TableCell
                                    key={header.id}
                                    sx={{
                                        border: '1px solid black',
                                        textAlign: 'center',
                                        padding: '8px'
                                    }}
                                >
                                    {header.id === 'actions' ? (
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    backgroundColor: '#4CAF50',
                                                    '&:hover': {
                                                        backgroundColor: '#45a049'
                                                    }
                                                }}
                                                onClick={handleSave}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    backgroundColor: '#f44336',
                                                    '&:hover': {
                                                        backgroundColor: '#da190b'
                                                    }
                                                }}
                                                onClick={handleCancel}
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    ) : (
                                        <TextField
                                            type={header.type === 'date' ? 'date' : 'text'}
                                            size="small"
                                            fullWidth
                                            value={newRowData[header.id]}
                                            onChange={(e) => handleNewRowChange(header.id, e.target.value)}
                                            // InputProps={{
                                            //     readOnly: autoCalculate && (header.id === 'gst' || header.id === 'qst')
                                            // }}
                                        />
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    )}
                    {purchaseData.map((row, index) => (
                        <TableRow key={row._id || index}>
                            {TABLE_HEADERS.map((header) => (
                                <TableCell
                                    key={header.id}
                                    sx={{
                                        border: '1px solid black',
                                        textAlign: 'center',
                                        padding: '8px'
                                    }}
                                >
                                    {header.id === 'actions' ? (
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                            {editingId === row._id ? (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: '#4CAF50',
                                                            '&:hover': {
                                                                backgroundColor: '#45a049'
                                                            }
                                                        }}
                                                        onClick={() => handleUpdate(row)}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: '#f44336',
                                                            '&:hover': {
                                                                backgroundColor: '#da190b'
                                                            }
                                                        }}
                                                        onClick={handleCancelEdit}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                (userInfo?.role==='admin' || accessControl["manage-purchase-expenses"])&&<Button
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: colors.primary,
                                                    }}
                                                    onClick={() => handleEdit(row)}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </Box>
                                    ) : (
                                        <TextField
                                            type={header.type === 'date' ? 'date' : 'text'}
                                            size="small"
                                            fullWidth
                                            value={header.type === 'date' ? formatDate(row[header.id]) : row[header.id]}
                                            onChange={(e) => handleInputChange(index, header.id, e.target.value)}
                                            // InputProps={{
                                            //     readOnly: (editingId !== row._id) || (autoCalculate && (header.id === 'gst' || header.id === 'qst'))
                                            // }}
                                        />
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
    
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2, alignItems: 'center', gap: 2 }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
                <CustomSelect
                    label="Rows per page"
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    options={[
                        { id: 100, name: '100' },
                        { id: 200, name: '200' },
                        { id: 300, name: '300' },
                        { id: 400, name: '400' },
                        { id: 500, name: '500' },
                    ]}
                    sx={{ minWidth: 120 }}
                />
            </Box>
        </TableContainer>
    );
};

export default PurchaseTable;
